import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { BundlesRepositoryService, ConfigurationState, eOrderType, eOrderTypeGroup, Order, OrderManagerService, OrderMessagesComponent, OrderRepositoryService, OrderStateRepositoryService, SelectOption } from "reg-hub-common";
import { Subject, Subscription, takeUntil, timer } from "rxjs";
import { OrdersHubService } from "../services/hubs/orders-hub/orders-hub.service";
import { NavigationHistoryService } from "../services/navigation-history-service/navigation-history.service";
import { ConfirmSubmitComponent } from "./confirm-submit/confirm-submit.component";
import { ConfirmDeleteComponent } from "./confirm-delete/confirm-delete.component";
import { ConfirmCancelComponent } from "./confirm-cancel/confirm-cancel.component";
import { AddToBundleComponent } from "./add-to-bundle/add-to-bundle.component";
import { Directive, OnDestroy, OnInit } from "@angular/core";

// TO DO: make new-registration-component and new-business-search-component use this base class
@Directive()
export abstract class NewOrderComponent implements OnInit, OnDestroy {
    orderId: string = "";
    order: Order = {
        referenceNumber: "",
        country: "CA",
        jurisdiction: "",
        orderTypeID: eOrderType.notSet,
    } as Order;

    orderTypeGroupID: eOrderTypeGroup = eOrderTypeGroup.PPSASearch;
    isLoadingOrder: boolean = false;
    finalStepIsSelected: boolean = false;
    showOrderFees: boolean = false;
    private bundleOptions: SelectOption[] = [];
    
    private onDestroy$ = new Subject<void>();
    private orderStateSubscription?: Subscription;

    constructor(private activatedRoute: ActivatedRoute,
        private orderRepo: OrderRepositoryService,
        private orderManager: OrderManagerService,
        private orderStateRepo: OrderStateRepositoryService,
        private dialog: MatDialog,
        private router: Router,
        private ordersHub: OrdersHubService,
        private navigationService: NavigationHistoryService,
        private configurationState: ConfigurationState,
        private bundlesRepo: BundlesRepositoryService) { }

    ngOnInit(): void {
        this.orderManager.currentOrder
            .pipe(takeUntil(this.onDestroy$))
            .subscribe(order => this.order = order);

        this.shouldShowOrderSummary();

        this.activatedRoute.paramMap.subscribe(params => {
            this.orderId = params.get('orderID')!;
            if (this.orderId) {
                // If we have an orderID, load the associated order and stepper
                this.getOrder(this.orderId);
            } else {
                this.resetOrder();
                // if we don't have an orderID, load base stepper
                this.orderManager.updateOrder(this.order);
            }
        });
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();

        if (this.orderStateSubscription) {
            this.orderStateSubscription.unsubscribe();
        }
    }

    protected getOrder(orderID: string) {
        // this wil call api/v1/corporations
        this.isLoadingOrder = true;
        this.orderRepo.getOrder('orders', orderID)
            .subscribe(order => {
                this.isLoadingOrder = false;
                this.order = order as Order;

                this.orderManager.updateOrder(this.order);
                this.getBundles();
            })
    }

    protected messageCount() {
        let length = this.order?.orderMessages?.filter((u) => u.isCustomerVisible && !u.isReadByCustomer).length ?? 0;
        return length > 0 ? length.toString() : "";
    }

    protected shouldShowOrderMessages(): boolean {
        return this.order.orderStatusTypeID == 'Invalid'
            || this.messageCount() != "";
    }

    protected shouldShowOrderSummary() {
        this.configurationState.hasOrderFeeVisibility().then(show => {
            this.showOrderFees = show;
        });
    }

    protected openOrderMessages() {
        this.dialog.open(OrderMessagesComponent, {
            data: { order: this.order, customer: true },
            minWidth: '800px'
        });
    }

    protected submissionConfirmation() {
        const dialogRef = this.dialog.open(ConfirmSubmitComponent, {
            data: this.order
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result.submit === true) {
                // submission is confirmed, submit order
                this.submitOrder();
            }
        });
    }

    protected submitOrder() {
        //Limit flashing of order spinner
        var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

        const pendingStatus = {
            newStatus: "Pending",
            status: "",
            message: "Submitted from Reg Hub Client"
        }

        this.orderStateSubscription = this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
            .subscribe({
                next: (result) => {
                    timerSubscription.unsubscribe();
                    this.isLoadingOrder = false;

                    if (result) {
                        this.ordersHub.watchOrder(this.order);
                        this.order.orderStatusTypeID = "Pending";
                        this.orderManager.updateOrder(this.order);
                        this.router.navigate(['orders', this.order.id]);
                    }
                },
                error: () => {
                    timerSubscription.unsubscribe();
                    this.isLoadingOrder = false;
                }
            });
    }

    protected deletionConfirmation() {
        if (this.order?.orderStatusTypeID == "Draft" ||
            this.order?.orderStatusTypeID == "HistoricalDraft") {
            const dialogRef = this.dialog.open(ConfirmDeleteComponent);

            dialogRef.afterClosed().subscribe(result => {
                if (result) {
                    // deletion is confirmed, delet order
                    this.deleteOrder();
                }
            });
        }
    }

    protected deleteOrder() {
        const orderID = this.order.id;

        this.orderRepo.deleteOrder("Orders/" + this.order.id)
            .subscribe({
                next: () => {
                    let previousUrl = this.navigationService.getPreviousUrl();

                    if (previousUrl?.toString()?.includes(orderID)) {
                        previousUrl = this.navigationService.getPreviousUrl(1);
                    }

                    this.router.navigateByUrl(previousUrl);
                },
                error: (error) => {
                    console.log(error);
                }
            });
    }

    protected cancelConfirmation() {
        const dialogRef = this.dialog.open(ConfirmCancelComponent);

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // deletion is confirmed, delet order
                this.cancelOrder();
            }
        });
    }

    protected cancelOrder() {
        var timerSubscription = timer(250).subscribe(() => this.isLoadingOrder = true);

        const pendingStatus = {
            newStatus: "Cancelled",
            status: "",
            message: "Cancelled from Reg Hub Client"
        }

        this.orderStateRepo.updateOrderState(this.order.id, pendingStatus)
            .subscribe({
                next: (result) => {
                    timerSubscription.unsubscribe();
                    this.isLoadingOrder = false;

                    if (!result) {
                        //this.resultPoller.startPolling(this.order.id);
                        this.order.orderStatusTypeID = "Cancelled";
                        this.orderManager.updateOrder(this.order);
                        this.router.navigate(['orders', this.order.id]);
                    }
                },
                error: () => {
                    timerSubscription.unsubscribe();
                    this.isLoadingOrder = false;
                }
            });
    }

    protected resetOrder() {
        this.order = {
            referenceNumber: "",
            country: "CA",
            jurisdiction: "",
            orderTypeID: eOrderType.notSet,
        } as Order;
    }

    protected getBundles() {
        if (this.order.orderGroup) {
            this.bundlesRepo.getBundlesAsSelectOptions(this.order.orderGroup!.corporationID).subscribe(bundles => {
                this.bundleOptions = bundles;
            });
        }
    }

    protected getAvailableBundles(): SelectOption[] {
        var orderBundles = this.order.orderGroup?.bundles?.map(bundle => bundle.id) ?? [];

        return this.bundleOptions.filter(opt => !orderBundles.includes(opt.value as string));
    }

    protected showBundlesButton() {
        let availableBundles = this.getAvailableBundles();

        return availableBundles.length > 0;
    }

    protected addToBundle() {
        const dialogRef = this.dialog.open(AddToBundleComponent, {
            data: { order: this.order, bundles: this.getAvailableBundles() },
            minWidth: '500px'
        });
    }
}